import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { UseRootStore } from "@/model/RootStore"
import * as apiClient from "@/utils/ApiClient"

type ProfilePhoneInfo = {
	phone?: string | null
	phoneNumber?: string | null
	phoneVerifiedAt?: Date | string | null
	phone_verified_at?: Date | string | null
}

const normalizePhoneNumberForComparison = (phoneNumber: string | null | undefined): string =>
	(phoneNumber ?? "").trim().replace(/[\s().-]/g, "")

const formatPhoneNumberForDisplay = (phoneNumber: string | null | undefined): string => {
	const normalized = normalizePhoneNumberForComparison(phoneNumber)
	const northAmericanMatch = normalized.match(/^\+1(\d{3})(\d{3})(\d{4})$/)
	return northAmericanMatch
		? `+1-${northAmericanMatch[1]}-${northAmericanMatch[2]}-${northAmericanMatch[3]}`
		: phoneNumber ?? ""
}

export function usePhoneVerification() {
	const store = UseRootStore()
	const { t, locale } = useI18n()

	const phoneNumberDraft = ref("")
	const phoneVerificationCode = ref("")
	const phoneVerificationRequestedFor = ref<string | null>(null)
	const phoneVerificationMessage = ref<string | null>(null)
	const phoneVerificationError = ref<string | null>(null)
	const isStartingPhoneVerification = ref(false)
	const isConfirmingPhoneVerification = ref(false)
	const isEditingPhoneNumber = ref(false)

	const currentUserDetails = computed(() => store.CurrentUserBasicDetails ?? store.CurrentUserDetails)
	const userInfo = computed(() => store.currentUser.getInfo())
	const profilePhoneInfo = computed<ProfilePhoneInfo>(() => (userInfo.value ?? {}) as ProfilePhoneInfo)
	const savedPhoneNumber = computed(
		() =>
			currentUserDetails.value?.phoneNumber ?? profilePhoneInfo.value.phone ?? profilePhoneInfo.value.phoneNumber ?? ""
	)
	const savedPhoneVerifiedAt = computed(
		() =>
			currentUserDetails.value?.phoneVerifiedAt ??
			profilePhoneInfo.value.phoneVerifiedAt ??
			profilePhoneInfo.value.phone_verified_at ??
			null
	)

	const hasPhoneNumberDraft = computed(() => phoneNumberDraft.value.trim().length > 0)
	const normalizedPhoneNumberDraft = computed(() => normalizePhoneNumberForComparison(phoneNumberDraft.value))
	const normalizedSavedPhoneNumber = computed(() => normalizePhoneNumberForComparison(savedPhoneNumber.value))
	const formattedSavedPhoneNumber = computed(() => formatPhoneNumberForDisplay(savedPhoneNumber.value))
	const phoneNumberMatchesSaved = computed(() => normalizedPhoneNumberDraft.value === normalizedSavedPhoneNumber.value)
	const phoneNumberDraftHasChanged = computed(
		() => normalizedPhoneNumberDraft.value !== normalizedSavedPhoneNumber.value
	)
	const isPhoneVerified = computed(
		() => hasPhoneNumberDraft.value && phoneNumberMatchesSaved.value && Boolean(savedPhoneVerifiedAt.value)
	)
	const isSmsPreferenceBlocked = computed(() => !isPhoneVerified.value)
	const isPhoneVerificationBusy = computed(
		() => isStartingPhoneVerification.value || isConfirmingPhoneVerification.value
	)
	const isPhoneVerificationPending = computed(
		() =>
			Boolean(phoneVerificationRequestedFor.value) &&
			normalizedPhoneNumberDraft.value === normalizePhoneNumberForComparison(phoneVerificationRequestedFor.value) &&
			!isPhoneVerified.value
	)
	const canStartPhoneVerification = computed(
		() => hasPhoneNumberDraft.value && !isPhoneVerified.value && !isPhoneVerificationBusy.value
	)
	const canConfirmPhoneVerification = computed(
		() =>
			isPhoneVerificationPending.value &&
			phoneVerificationCode.value.trim().length > 0 &&
			!isPhoneVerificationBusy.value
	)
	const showVerifiedPhoneSummary = computed(() => isPhoneVerified.value && !isEditingPhoneNumber.value)
	const showCancelPhoneChange = computed(() => isEditingPhoneNumber.value && !isPhoneVerificationBusy.value)
	const showStartPhoneVerificationButton = computed(
		() => !isPhoneVerified.value || (isEditingPhoneNumber.value && phoneNumberDraftHasChanged.value)
	)

	const formattedPhoneVerifiedAt = computed(() => {
		if (!savedPhoneVerifiedAt.value) return ""

		const date =
			savedPhoneVerifiedAt.value instanceof Date ? savedPhoneVerifiedAt.value : new Date(savedPhoneVerifiedAt.value)
		return Number.isNaN(date.getTime())
			? ""
			: new Intl.DateTimeFormat(locale.value === "en" ? "en-CA" : "fr-CA", { dateStyle: "medium" }).format(date)
	})

	const phoneStatusLabel = computed(() => {
		if (!hasPhoneNumberDraft.value) return t("my-profile.phone.status.missing")
		return isPhoneVerified.value ? t("my-profile.phone.status.verified") : t("my-profile.phone.status.unverified")
	})

	const phoneNote = computed(() => {
		if (!hasPhoneNumberDraft.value) return t("my-profile.phone.note.empty")
		if (isPhoneVerified.value && formattedPhoneVerifiedAt.value) {
			return t("my-profile.phone.note.verified", { date: formattedPhoneVerifiedAt.value })
		}
		if (savedPhoneVerifiedAt.value && !phoneNumberMatchesSaved.value) return t("my-profile.phone.note.changed")
		return t("my-profile.phone.note.unverified")
	})

	const startPhoneVerificationButtonLabel = computed(() => {
		if (isStartingPhoneVerification.value) return t("my-profile.phone.actions.sending")
		if (isPhoneVerificationPending.value) return t("my-profile.phone.actions.resend")
		return t("my-profile.phone.actions.verify")
	})

	const confirmPhoneVerificationButtonLabel = computed(() =>
		isConfirmingPhoneVerification.value
			? t("my-profile.phone.actions.confirming")
			: t("my-profile.phone.actions.confirm")
	)

	const resetPhoneVerificationState = () => {
		phoneVerificationRequestedFor.value = null
		phoneVerificationCode.value = ""
		phoneVerificationMessage.value = null
		phoneVerificationError.value = null
	}

	const phoneVerificationErrorMessage = (error: unknown, fallbackKey: string) =>
		error instanceof Error ? error.message : t(fallbackKey)

	const editPhoneNumber = () => {
		isEditingPhoneNumber.value = true
		phoneVerificationMessage.value = null
		phoneVerificationError.value = null
	}

	const cancelPhoneNumberChange = () => {
		phoneNumberDraft.value = savedPhoneNumber.value
		isEditingPhoneNumber.value = false
		resetPhoneVerificationState()
	}

	const startPhoneVerificationFlow = async () => {
		if (!store.isUserLoggedIn || !canStartPhoneVerification.value) return

		isStartingPhoneVerification.value = true
		phoneVerificationMessage.value = null
		phoneVerificationError.value = null
		try {
			const result = await apiClient.startPhoneVerification(store.userApiToken, phoneNumberDraft.value)
			phoneNumberDraft.value = result.phoneNumber
			phoneVerificationRequestedFor.value = result.phoneNumber
			phoneVerificationCode.value = ""
			isEditingPhoneNumber.value = true
			phoneVerificationMessage.value = t("my-profile.phone.messages.code-sent")
		} catch (error) {
			phoneVerificationError.value = phoneVerificationErrorMessage(error, "my-profile.phone.messages.start-failed")
		} finally {
			isStartingPhoneVerification.value = false
		}
	}

	const confirmPhoneVerificationFlow = async () => {
		if (!store.isUserLoggedIn || !canConfirmPhoneVerification.value) return

		isConfirmingPhoneVerification.value = true
		phoneVerificationMessage.value = null
		phoneVerificationError.value = null
		try {
			const result = await apiClient.confirmPhoneVerification(
				store.userApiToken,
				phoneNumberDraft.value,
				phoneVerificationCode.value
			)

			if (!result.approved) {
				phoneVerificationError.value = t("my-profile.phone.messages.invalid-code")
				return
			}

			store.CurrentUserBasicDetails = {
				...(store.CurrentUserBasicDetails ?? {}),
				phoneNumber: result.phoneNumber,
				phoneVerifiedAt: result.phoneVerifiedAt ?? new Date().toISOString(),
			}
			if (store.CurrentUserDetails) {
				store.CurrentUserDetails = {
					...store.CurrentUserDetails,
					phoneNumber: result.phoneNumber,
					phoneVerifiedAt: result.phoneVerifiedAt ?? new Date().toISOString(),
				}
			}
			phoneNumberDraft.value = result.phoneNumber
			isEditingPhoneNumber.value = false
			resetPhoneVerificationState()
			phoneVerificationMessage.value = t("my-profile.phone.messages.verified")
			await store.fetchBasicCurrentUserDetails()
		} catch (error) {
			phoneVerificationError.value = phoneVerificationErrorMessage(error, "my-profile.phone.messages.confirm-failed")
		} finally {
			isConfirmingPhoneVerification.value = false
		}
	}

	watch(
		savedPhoneNumber,
		phoneNumber => {
			if (!isEditingPhoneNumber.value) {
				phoneNumberDraft.value = phoneNumber
			}
		},
		{ immediate: true }
	)

	watch(phoneNumberDraft, phoneNumber => {
		if (
			phoneVerificationRequestedFor.value &&
			normalizePhoneNumberForComparison(phoneNumber) !==
				normalizePhoneNumberForComparison(phoneVerificationRequestedFor.value)
		) {
			resetPhoneVerificationState()
		}
	})

	return {
		phoneNumberDraft,
		phoneVerificationCode,
		phoneVerificationMessage,
		phoneVerificationError,
		isStartingPhoneVerification,
		isConfirmingPhoneVerification,
		isEditingPhoneNumber,
		hasPhoneNumberDraft,
		formattedSavedPhoneNumber,
		isPhoneVerified,
		isSmsPreferenceBlocked,
		isPhoneVerificationBusy,
		isPhoneVerificationPending,
		canStartPhoneVerification,
		canConfirmPhoneVerification,
		showVerifiedPhoneSummary,
		showCancelPhoneChange,
		showStartPhoneVerificationButton,
		formattedPhoneVerifiedAt,
		phoneStatusLabel,
		phoneNote,
		startPhoneVerificationButtonLabel,
		confirmPhoneVerificationButtonLabel,
		editPhoneNumber,
		cancelPhoneNumberChange,
		startPhoneVerificationFlow,
		confirmPhoneVerificationFlow,
	}
}
